import { api } from '../api.js';
import { getSessionScope } from '../app/sessionScope.js';

const DATABASE_NAME = 'byggo-company-offline-v1';
const STORE_NAME = 'mutations';
const FALLBACK_KEY = 'byggo_company_offline_mutations_v1';
export const OFFLINE_QUEUE_EVENT = 'byggo:offline-queue';

let processing = false;
const inFlight = new Set();
const MAX_QUEUE_AGE_MS = 30 * 86_400_000;

export function isTransientMutationError(error) {
  const status = Number(error?.status || 0);
  return error?.name === 'AbortError' || !status || status >= 500
    || [401, 408, 419, 429].includes(status)
    || (status === 409 && error?.code === 'idempotency_request_in_progress');
}

function assertOwner(ownerKey) {
  if (!/^\d+:\d+$/.test(ownerKey || '') || getSessionScope().ownerKey !== ownerKey) {
    throw new DOMException('Logga in i företaget som underlaget tillhör innan det synkas.', 'AbortError');
  }
}

export function holdOfflineMutation(itemId) {
  inFlight.add(itemId);
  return () => inFlight.delete(itemId);
}

function id() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `offline-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function emit(detail = {}) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OFFLINE_QUEUE_EVENT, { detail }));
  }
}

function hasIndexedDb() {
  return typeof indexedDB !== 'undefined';
}

function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, 1);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('owner_key', 'owner_key', { unique: false });
        store.createIndex('status', 'status', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function indexedDbAction(mode, callback) {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    let request;
    try {
      request = callback(store);
    } catch (error) {
      database.close();
      reject(error);
      return;
    }
    transaction.oncomplete = () => {
      database.close();
      resolve(request?.result);
    };
    transaction.onerror = () => {
      database.close();
      reject(transaction.error);
    };
    transaction.onabort = () => {
      database.close();
      reject(transaction.error || new Error('Ändringen kunde inte sparas på enheten.'));
    };
  });
}

function readFallback() {
  if (typeof localStorage === 'undefined') throw new Error('Lokal lagring är inte tillgänglig. Underlaget har inte sparats.');
  try {
    const value = JSON.parse(localStorage.getItem(FALLBACK_KEY) || '[]');
    if (!Array.isArray(value)) throw new Error('Invalid offline queue');
    return value;
  } catch {
    throw new Error('Sparade offlineunderlag kunde inte läsas. De har lämnats orörda.');
  }
}

function writeFallback(items) {
  if (typeof localStorage === 'undefined') throw new Error('Lokal lagring är inte tillgänglig. Underlaget har inte sparats.');
  localStorage.setItem(FALLBACK_KEY, JSON.stringify(items));
}

async function allItems() {
  if (hasIndexedDb()) return (await indexedDbAction('readonly', (store) => store.getAll())) || [];
  return readFallback();
}

export async function pendingCompanyMutations(ownerKey) {
  return (await allItems()).filter(item => item.owner_key === ownerKey).length;
}

async function putItem(item) {
  if (hasIndexedDb()) {
    await indexedDbAction('readwrite', (store) => store.put(item));
    return item;
  }
  const items = readFallback().filter((candidate) => candidate.id !== item.id);
  items.push(item);
  writeFallback(items);
  return item;
}

async function deleteItem(itemId) {
  if (hasIndexedDb()) {
    await indexedDbAction('readwrite', (store) => store.delete(itemId));
    return;
  }
  writeFallback(readFallback().filter((item) => item.id !== itemId));
}

export async function enqueueOfflineMutation({ path, method = 'POST', body = {}, ownerKey, label = 'Ändring', operationId = id() }) {
  assertOwner(ownerKey);
  const existing = (await allItems()).find((item) => item.owner_key === ownerKey && item.status === 'pending'
    && item.path === path && item.method === String(method).toUpperCase() && JSON.stringify(item.body) === JSON.stringify(body));
  assertOwner(ownerKey);
  if (existing) return existing;
  const item = {
    id: operationId,
    owner_key: ownerKey,
    path,
    method: String(method).toUpperCase(),
    body,
    label,
    status: 'pending',
    attempts: 0,
    last_error: '',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  await putItem(item);
  assertOwner(ownerKey);
  emit({ action: 'queued', item });
  return item;
}

export async function offlineQueueSummary(ownerKey) {
  const items = ownerKey ? (await allItems()).filter((item) => item.owner_key === ownerKey) : [];
  return {
    items,
    pending: items.filter((item) => item.status === 'pending').length,
    failed: items.filter((item) => item.status === 'failed').length,
    total: items.length,
    processing,
  };
}

export async function completeOfflineMutation(item) {
  await deleteItem(item.id);
  emit({ action: 'synced', item });
}

export async function recordOfflineFailure(item, error) {
  const transient = isTransientMutationError(error);
  await putItem({
    ...item,
    status: transient ? 'pending' : 'failed',
    retryable: transient,
    attempts: Number(item.attempts || 0) + 1,
    last_error: error?.message || 'Synkningen misslyckades.',
    error_code: error?.code || '',
    next_attempt_at: new Date(Date.now() + Math.max(Number(error?.retryAfterMs || 0), 5000)).toISOString(),
    updated_at: new Date().toISOString(),
  });
  emit({ action: transient ? 'sync-paused' : 'sync-failed', item });
}

export async function retryFailedOfflineMutations(ownerKey) {
  assertOwner(ownerKey);
  const items = (await allItems()).filter((item) => item.owner_key === ownerKey && item.status === 'failed' && item.retryable !== false);
  await Promise.all(items.map((item) => putItem({ ...item, status: 'pending', last_error: '', updated_at: new Date().toISOString() })));
  emit({ action: 'retry', count: items.length });
  return processOfflineMutations(ownerKey);
}

export async function processOfflineMutations(ownerKey) {
  if (processing || !ownerKey || (typeof navigator !== 'undefined' && !navigator.onLine)) {
    return offlineQueueSummary(ownerKey);
  }

  assertOwner(ownerKey);
  const generation = getSessionScope().generation;

  processing = true;
  emit({ action: 'sync-started' });
  try {
    const items = (await allItems())
      .filter((item) => item.owner_key === ownerKey && item.status === 'pending')
      .sort((left, right) => String(left.created_at).localeCompare(String(right.created_at)));

    for (const item of items) {
      if (getSessionScope().generation !== generation || getSessionScope().ownerKey !== ownerKey) break;
      if (inFlight.has(item.id)) continue;
      if (Date.now() - Date.parse(item.created_at) > MAX_QUEUE_AGE_MS) {
        await recordOfflineFailure(item, { status: 422, code: 'offline_review_required', message: 'Underlaget är äldre än 30 dagar. Kontrollera om det redan har sparats innan du skickar det på nytt.' });
        continue;
      }
      if (Date.parse(item.next_attempt_at || '') > Date.now()) continue;
      const release = holdOfflineMutation(item.id);
      try {
        await api(item.path, {
          method: item.method,
          body: JSON.stringify(item.body),
          headers: { 'Idempotency-Key': item.id },
        });
        await completeOfflineMutation(item);
      } catch (error) {
        await recordOfflineFailure(item, error);
        if (isTransientMutationError(error)) break;
      } finally {
        release();
      }
    }
  } finally {
    processing = false;
    emit({ action: 'sync-finished' });
  }

  return offlineQueueSummary(ownerKey);
}

export async function clearOfflineQueue(ownerKey) {
  const items = (await allItems()).filter((item) => !ownerKey || item.owner_key === ownerKey);
  await Promise.all(items.map((item) => deleteItem(item.id)));
  emit({ action: 'cleared', count: items.length });
}
